import {
  Student, UsersThree, Buildings, IdentificationCard, FileText, ShieldCheck,
  Lightning, ClockCountdown, HandCoins, ChartLineUp, Files, Handshake,
  Eye, ClipboardText, CheckCircle,
} from "@phosphor-icons/react";

export const AUDIENCES = {
  student: {
    key: "student",
    label: "Students",
    icon: Student,
    to: "/student",
    image: "https://images.pexels.com/photos/5158946/pexels-photo-5158946.jpeg",
    hero: "APPLY ONCE. LIVE ANYWHERE.",
    tagline: "For Students",
    intro:
      "Stop filling out the same application for every single property. Build one verified OneLease profile and use it everywhere.",
    steps: [
      { icon: IdentificationCard, title: "Create one profile", text: "Add your details, documents and roommates once. It's yours to reuse forever." },
      { icon: FileText, title: "Submit one application", text: "Send your standardized application to any participating property in a single tap." },
      { icon: UsersThree, title: "Build your group", text: "Invite roommates, apply together, and keep everyone on the same page." },
      { icon: Lightning, title: "Get approved faster", text: "Verified profiles move to the front of the line with standardized screening." },
    ],
    values: [
      { icon: FileText, title: "No repetitive forms", text: "One application replaces the dozen you'd normally fill out each leasing season." },
      { icon: ShieldCheck, title: "Verified once, trusted everywhere", text: "Your identity and income checks travel with you across every property." },
      { icon: ClockCountdown, title: "Faster decisions", text: "Standardized data means managers can say yes in hours, not weeks." },
    ],
    cta: "Skip the paperwork forever",
  },
  parent: {
    key: "parent",
    label: "Parents",
    icon: UsersThree,
    to: "/parent",
    image: "https://images.unsplash.com/photo-1629360067822-89c74b25bb66?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxwYXJlbnRzJTIwaGVscGluZyUyMHN0dWRlbnR8ZW58MHx8fHwxNzg0ODUzNTU1fDA&ixlib=rb-4.1.0&q=85",
    hero: "UPLOAD ONCE. CO-SIGN WITH CONFIDENCE.",
    tagline: "For Parents",
    intro:
      "No more re-uploading pay stubs and bank statements for every property. OneLease centralizes verification and makes co-signing effortless.",
    steps: [
      { icon: Files, title: "Upload documents once", text: "Securely add financial documents a single time to a private, encrypted profile." },
      { icon: ShieldCheck, title: "Verify centrally", text: "Income and identity are checked once and reused across every application." },
      { icon: Eye, title: "Stay transparent", text: "See exactly where your student's application stands, in real time." },
      { icon: Handshake, title: "Approve in a tap", text: "Digital approvals with zero paperwork." },
    ],
    values: [
      { icon: Files, title: "Stop re-uploading", text: "Your status is saved once and works for all properties." },
      { icon: ShieldCheck, title: "Secure & minimal", text: "We store verification results, not documents or sensitive financials." },
      { icon: Eye, title: "Full visibility", text: "Track approvals and communicate with property managers from one dashboard." },
    ],
    cta: "Make co-signing painless",
  },
  "property-manager": {
    key: "property-manager",
    label: "Property Managers",
    icon: Buildings,
    to: "/property-manager",
    image: "https://images.pexels.com/photos/8730042/pexels-photo-8730042.jpeg",
    hero: "STANDARDIZED APPLICANTS. FASTER APPROVALS.",
    tagline: "For Property Managers",
    intro:
      "Stop manually verifying dozens of applicants with inconsistent information. OneLease delivers standardized, pre-verified applications you can trust.",
    steps: [
      { icon: ClipboardText, title: "Receive standardized apps", text: "Every applicant arrives in the same clean, complete, comparable format." },
      { icon: ShieldCheck, title: "Skip manual verification", text: "Identity and income are pre-verified, cutting your screening workload dramatically." },
      { icon: CheckCircle, title: "Assess quality instantly", text: "Consistent data surfaces the strongest applicants at a glance." },
      { icon: Lightning, title: "Approve faster", text: "Fill units quicker with confident, defensible, faster decisions." },
    ],
    values: [
      { icon: ClipboardText, title: "One consistent format", text: "No more chasing missing fields or deciphering mismatched documents." },
      { icon: HandCoins, title: "Lower risk", text: "Pre-verified applicants and standardized screening reduce defaults and disputes." },
      { icon: ChartLineUp, title: "Higher throughput", text: "Process more applicants in less time and keep occupancy high." },
    ],
    cta: "Cut your verification workload",
  },
};

export const AUDIENCE_LIST = [AUDIENCES.student, AUDIENCES.parent, AUDIENCES["property-manager"]];

export const FAQS = [
  { q: "What is OneLease?", a: "OneLease is a unified student-housing application platform that lets students, parents, and property managers use one profile, one verification, and one standardized application across every participating property." },
  { q: "How does the OneLease application work?", a: "You complete a single profile and verification once. After that, you can apply to any participating property instantly without repeating forms or re-uploading documents." },
  { q: "Can I apply with roommates?", a: "Yes. OneLease lets you build a verified roommate group so properties can review your entire household together." },
  { q: "Is my information secure?", a: "Yes. OneLease uses industry-standard security practices and never sells your data. Only verification statuses are stored." },
  { q: "How much does OneLease cost?", a: "We're still finalizing our pricing model as we onboard properties and refine the platform. Students and parents will always be able to join the waitlist for free. Join the waitlist to stay updated as pricing evolves." },
  { q: "Does OneLease replace my leasing company?", a: "No. Leasing companies still handle leases, payments, move-ins, and renewals. OneLease simply standardizes and streamlines the application and verification process." },
  { q: "What information do I need to apply?", a: "Students provide basic personal info, academic status, and guarantor details. Parents/guarantors provide identity and income verification. No sensitive documents are collected on the marketing site." },
  { q: "What happens after I submit an application?", a: "Your unified profile is sent directly to the property's leasing team. They review your group, verification statuses, and application details, then contact you with next steps." },
  { q: "Is OneLease available at my property yet?", a: "We're onboarding student-housing communities throughout 2026. Join the waitlist to be notified when your city or property goes live." },
  { q: "Who do I contact for support?", a: "You can reach us anytime at OneLease.app@gmail.com for questions about the platform, onboarding, or waitlist status." },
];
